import { useEffect, useState } from "react";
import { ShieldCheck, Smartphone } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls =
  "w-full bg-transparent border border-white/10 px-4 py-3 text-sm text-white placeholder:text-white/30 gold-focus transition-shadow";
const btnCls =
  "shrink-0 border border-[#D4AF37]/60 text-[#D4AF37] px-4 font-accent text-[10px] tracking-[0.2em] uppercase hover:bg-[#D4AF37] hover:text-black transition-colors disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-[#D4AF37]";

export const PhoneVerify = ({ phone, email, onPhone, verified, onVerified }) => {
  const [sent, setSent] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [channel, setChannel] = useState("sms");
  const viaEmail = channel === "email";

  useEffect(() => {
    api.get("/auth/otp/channel").then((r) => setChannel(r.data.channel)).catch(() => {});
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const send = async () => {
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/otp/send", { phone, email: email || null });
      setSent(data);
      setCooldown(data.resend_in);
      setCode("");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/otp/verify", { phone, code });
      onVerified(data.phone_token, data.phone);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (verified) {
    return (
      <div className="flex items-center justify-between border border-emerald-400/40 bg-emerald-400/5 px-4 py-3" data-testid="phone-verified-badge">
        <span className="flex items-center gap-2 text-sm text-emerald-300"><ShieldCheck size={15} /> {phone} verified</span>
        <button type="button" data-testid="phone-change-button" onClick={() => { onVerified("", phone); setSent(null); }} className="text-xs text-white/40 hover:text-[#D4AF37] transition-colors">Change</button>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="phone-verify">
      <p className="font-accent text-[9px] tracking-[0.2em] uppercase text-white/35" data-testid="phone-verify-label">
        {viaEmail ? "Step 2 · Verify via email code" : "Step 2 · Verify mobile via SMS code"}
      </p>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Smartphone size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
          <input data-testid="register-phone-input" type="tel" inputMode="numeric" required placeholder="Mobile number (10 digits)" value={phone} onChange={(e) => onPhone(e.target.value.replace(/[^\d+]/g, ""))} disabled={!!sent} className={`${inputCls} pl-10 disabled:opacity-60`} />
        </div>
        <button type="button" data-testid="otp-send-button" onClick={send} disabled={busy || phone.replace(/\D/g, "").length < 10 || (viaEmail && !/\S+@\S+\.\S+/.test(email || "")) || cooldown > 0} className={btnCls}>
          {cooldown > 0 ? `Resend ${cooldown}s` : sent ? "Resend" : viaEmail ? "Email code" : "Send OTP"}
        </button>
      </div>
      {viaEmail && !sent && (
        <p className="text-xs text-white/40" data-testid="phone-verify-email-hint">We'll email a 6-digit code to <span className="text-white/70">{email || "your email address"}</span> to confirm this mobile number. Enter your email above first.</p>
      )}
      {sent && (
        <>
          <p className="text-xs text-white/50" data-testid="otp-sent-message">{sent.message}. Code valid for {Math.round(sent.expires_in / 60)} minutes.{sent.channel === "email" && " Check your inbox and spam folder."}</p>
          <div className="flex gap-2">
            <input data-testid="otp-code-input" inputMode="numeric" maxLength={6} placeholder="6-digit code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className={`${inputCls} font-mono tracking-[0.5em]`} autoFocus />
            <button type="button" data-testid="otp-verify-button" onClick={verify} disabled={busy || code.length !== 6} className={btnCls}>Verify</button>
          </div>
          {sent && (
            <button type="button" data-testid="otp-edit-phone-button" onClick={() => setSent(null)} className="text-xs text-white/40 hover:text-[#D4AF37] transition-colors">Edit number</button>
          )}
        </>
      )}
      {error && <p data-testid="otp-error" className="text-xs text-red-400">{error}</p>}
    </div>
  );
};
